import React, { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { q } from '@actual-app/core/shared/query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAccounts } from '#hooks/useAccounts';
import { useFormat } from '#hooks/useFormat';
import { useNavigate } from '#hooks/useNavigate';
import { usePrivacyMode } from '#hooks/usePrivacyMode';
import { useQuery } from '#hooks/useQuery';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import {
  shiftMonth,
  summarizeDashboard,
  transactionKind,
} from './dashboardData';
import type { DashboardTransaction } from './dashboardData';
import './xiaohua.css';

const palette = [
  '#40dcb1',
  '#50baff',
  '#a88cff',
  '#f6c76a',
  '#fc8d9b',
  '#7f96b8',
];
const grid = { stroke: '#28405a', strokeOpacity: 0.55, strokeDasharray: '3 5' };
const axis = { fill: '#8499b5', fontSize: 11 };
const tooltipStyle = {
  background: '#102337',
  border: '1px solid #32566c',
  borderRadius: 12,
  color: '#e9f5ff',
};

function Icon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    flower: (
      <>
        <path d="M12 12C2 10 5 0 12 7c7-7 10 3 0 5Z" />
        <path d="M12 12c10 2 7 12 0 5-7 7-10-3 0-5Z" />
      </>
    ),
    overview: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
    trend: (
      <>
        <path d="M3 18 9 12l4 3 8-10M15 5h6v6" />
      </>
    ),
    down: (
      <>
        <path d="M3 5 9 11l4-3 8 10M15 18h6v-6" />
      </>
    ),
    wallet: (
      <>
        <path d="M20 7V4H5a2 2 0 0 0 0 4h16v12H5a2 2 0 0 1-2-2V6" />
        <path d="M16 12h5v4h-5z" />
      </>
    ),
    chart: (
      <>
        <path d="M4 3v18h17M8 16v-5m5 5V6m5 10v-8" />
      </>
    ),
    layers: (
      <>
        <path d="m12 3 9 5-9 5-9-5 9-5ZM3 12l9 5 9-5M3 16l9 5 9-5" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    arrow: <path d="M5 12h14m-5-5 5 5-5 5" />,
    settings: (
      <>
        <path d="M4 7h16M4 17h16M8 4v6M16 14v6" />
        <circle cx="8" cy="7" r="2" />
        <circle cx="16" cy="17" r="2" />
      </>
    ),
    eye: (
      <>
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
  };
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths.overview}
    </svg>
  );
}

function ChartEmpty({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="xh-chart-empty">
      <span className="xh-empty-icon">
        <Icon name="chart" />
      </span>
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}

export default function XiaohuaDashboard() {
  const today = new Date();
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const currentDay = `${currentMonth}-${String(today.getDate()).padStart(2, '0')}`;
  const [month, setMonth] = useState(currentMonth);
  const start = `${shiftMonth(month, -11)}-01`;
  const nextMonth = shiftMonth(month, 1);
  const navigate = useNavigate();
  const { isNarrowWidth } = useResponsive();
  const dispatch = useDispatch();
  const format = useFormat();
  const privacy = usePrivacyMode();
  const [, setPrivacy] = useSyncedPref('isPrivacyEnabled');
  const accounts = useAccounts();
  const transactions = useQuery<DashboardTransaction>(
    () =>
      q('transactions')
        .options({ splits: 'inline' })
        .filter({
          $and: [
            { date: { $gte: start } },
            { date: { $lt: `${nextMonth}-01` } },
            { date: { $lte: currentDay } },
            {
              $or: [
                { starting_balance_flag: false },
                { starting_balance_flag: null },
              ],
            },
          ],
          'payee.transfer_acct': null,
        })
        .select([
          'id',
          'parent_id',
          'date',
          'amount',
          'category',
          'account',
          'notes',
          { categoryName: 'category.name' },
          { categoryIncome: 'category.is_income' },
          { accountName: 'account.name' },
          { payeeName: 'payee.name' },
        ]),
    [start, nextMonth, currentDay],
  );
  const data = useMemo(
    () => summarizeDashboard(transactions.data || [], month),
    [transactions.data, month],
  );
  const activeAccounts = (accounts.data || []).filter(
    account => !account.closed,
  );
  const loading = transactions.isLoading && !transactions.error;
  const failed = !!transactions.error;
  const hidden = privacy || loading || failed;
  const money = (amount: number) =>
    hidden ? '— —' : format(amount, 'financial');
  const chartMoney = (amount: number) =>
    format(amount, 'financial-no-decimals');
  const monthLabel = `${month.slice(0, 4)}年${Number(month.slice(5))}月`;
  const go = (path: string) => void navigate(path);
  const addEntry = () => {
    if (activeAccounts.length > 0) {
      if (isNarrowWidth)
        void navigate('/transactions/new', {
          state: { accountId: activeAccounts[0].id },
        });
      else go(`/accounts/${activeAccounts[0].id}`);
    } else if (!accounts.isLoading && !accounts.error)
      dispatch(pushModal({ modal: { name: 'add-account', options: {} } }));
  };
  const chartState = (
    empty: boolean,
    content: ReactNode,
    emptyTitle = '等待第一笔记录',
  ) => {
    if (failed)
      return (
        <ChartEmpty title="暂时无法读取账本">
          请重新打开账本后再试；现有记录不会被改变。
        </ChartEmpty>
      );
    if (loading)
      return <ChartEmpty title="正在读取账本">真实收支正在汇总。</ChartEmpty>;
    if (privacy)
      return (
        <ChartEmpty title="金额已隐藏">
          点击右上角“显示金额”查看本页数据。
        </ChartEmpty>
      );
    if (empty)
      return (
        <ChartEmpty title={emptyTitle}>录入收支后，图表会自动更新。</ChartEmpty>
      );
    return content;
  };

  return (
    <main className="xh-dashboard" aria-label="财务小花数据看板">
      <div className="xh-shell">
        <div className="xh-topline">
          <div className="xh-brand">
            <span className="xh-logo">
              <Icon name="flower" />
            </span>
            <span>
              财务小花 <small>1.0</small>
            </span>
          </div>
          <button
            type="button"
            className="xh-privacy"
            onClick={() => setPrivacy(String(!privacy))}
          >
            <Icon name="eye" />
            {privacy ? '显示金额' : '隐藏金额'}
          </button>
        </div>

        <header className="xh-header">
          <div>
            <div className="xh-eyebrow">FINANCIAL OVERVIEW</div>
            <h1>
              每一笔，都心中有数<span>。</span>
            </h1>
            <p>把日常收支整理清楚，让生活更从容。</p>
          </div>
          <button
            type="button"
            className="xh-button xh-button-primary"
            disabled={accounts.isLoading || !!accounts.error}
            onClick={addEntry}
          >
            <Icon name="plus" />
            {activeAccounts.length ? '录入收支' : '创建记账账户'}
          </button>
        </header>

        <div className="xh-toolbar">
          <nav className="xh-nav" aria-label="财务功能">
            <button className="active" type="button" aria-current="page">
              <Icon name="overview" />
              数据看板
            </button>
            <button type="button" onClick={() => go('/accounts')}>
              <Icon name="wallet" />
              收支管理
            </button>
            <button type="button" onClick={() => go('/budget')}>
              <Icon name="layers" />
              预算分类
            </button>
            <button type="button" onClick={() => go('/reports')}>
              <Icon name="chart" />
              统计分析
            </button>
          </nav>
          <label className="xh-month">
            <span>统计月份</span>
            <input
              type="month"
              aria-label="统计月份"
              value={month}
              max={currentMonth}
              min="1900-01"
              onChange={event => {
                const value = event.target.value;
                if (
                  /^\d{4}-(0[1-9]|1[0-2])$/.test(value) &&
                  value >= '1900-01' &&
                  value <= currentMonth
                )
                  setMonth(value);
              }}
            />
          </label>
        </div>

        {failed && (
          <div className="xh-error" role="alert">
            账本读取失败，当前未显示统计结果。请重新打开账本后重试。
          </div>
        )}
        {accounts.error && (
          <div className="xh-error" role="alert">
            账户列表读取失败，请重新打开账本后重试。
          </div>
        )}

        <section className="xh-stats" aria-label={`${monthLabel}收支汇总`}>
          {[
            {
              label: '月度净收入',
              value: money(data.income),
              caption: '已扣除收入冲正',
              icon: 'trend',
              tone: 'green',
            },
            {
              label: '月度净支出',
              value: money(data.expense),
              caption: '支出发生额 − 退款',
              icon: 'down',
              tone: 'blue',
            },
            {
              label: '月度结余',
              value: money(data.net),
              caption: '净收入 − 净支出',
              icon: 'wallet',
              tone: 'purple',
            },
            {
              label: '记账笔数',
              value: hidden ? '— —' : `${data.transactionCount}`,
              caption: `${activeAccounts.length} 个记账账户`,
              icon: 'layers',
              tone: 'amber',
            },
          ].map(stat => (
            <article className={`xh-stat xh-${stat.tone}`} key={stat.label}>
              <div className="xh-stat-label">
                <span>{stat.label}</span>
                <span className="xh-stat-icon">
                  <Icon name={stat.icon} />
                </span>
              </div>
              <strong>{stat.value}</strong>
              <small>{stat.caption}</small>
            </article>
          ))}
        </section>

        <div className="xh-grid">
          <section className="xh-panel xh-trend">
            <div className="xh-panel-heading">
              <div>
                <h2>收支趋势</h2>
                <p>截至 {monthLabel} · 近 12 个月</p>
              </div>
              <div className="xh-chart-legend">
                <span>
                  <i style={{ background: palette[0] }} />
                  净收入
                </span>
                <span>
                  <i style={{ background: palette[1] }} />
                  净支出
                </span>
              </div>
            </div>
            <div className="xh-chart" aria-label="近12个月收支趋势图">
              {chartState(
                !data.hasHistory,
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={data.months}
                    margin={{ top: 15, right: 8, bottom: 0, left: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="xh-income"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={palette[0]}
                          stopOpacity={0.25}
                        />
                        <stop
                          offset="100%"
                          stopColor={palette[0]}
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="xh-expense"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={palette[1]}
                          stopOpacity={0.15}
                        />
                        <stop
                          offset="100%"
                          stopColor={palette[1]}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...grid} vertical={false} />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={axis}
                      minTickGap={16}
                      dy={6}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={axis}
                      width={65}
                      tickFormatter={chartMoney}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      labelFormatter={(_, items) =>
                        items?.[0]?.payload?.month || ''
                      }
                      formatter={(value, name) => [
                        format(Number(value), 'financial'),
                        name === 'income' ? '净收入' : '净支出',
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="income"
                      stroke={palette[0]}
                      fill="url(#xh-income)"
                      strokeWidth={2.5}
                      isAnimationActive={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="expense"
                      stroke={palette[1]}
                      fill="url(#xh-expense)"
                      strokeWidth={2.5}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>,
              )}
            </div>
          </section>

          <section className="xh-panel xh-category">
            <div className="xh-panel-heading">
              <div>
                <h2>支出分类</h2>
                <p>{monthLabel} · 支出发生额，退款另计</p>
              </div>
              <span className="xh-panel-mark">02</span>
            </div>
            {chartState(
              !data.grossExpense,
              <div className="xh-donut-section">
                <div className="xh-donut">
                  <ResponsiveContainer width="100%" height={210}>
                    <PieChart>
                      <Pie
                        data={data.categories}
                        dataKey="amount"
                        nameKey="name"
                        innerRadius={68}
                        outerRadius={87}
                        paddingAngle={3}
                        stroke="none"
                        isAnimationActive={false}
                      >
                        {data.categories.map((category, index) => (
                          <Cell key={category.id} fill={palette[index]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={value => format(Number(value), 'financial')}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="xh-donut-total">
                    <span>退款前支出</span>
                    <strong>{money(data.grossExpense)}</strong>
                  </div>
                </div>
                <ul className="xh-category-list">
                  {data.categories.map((category, index) => (
                    <li key={category.id}>
                      <span>
                        <i style={{ background: palette[index] }} />
                        {category.name}
                      </span>
                      <strong>
                        {Math.round(
                          (category.amount / data.grossExpense) * 100,
                        )}
                        <small>%</small>
                      </strong>
                    </li>
                  ))}
                </ul>
              </div>,
              '本月没有支出发生额',
            )}
            {!hidden && data.refunds > 0 && (
              <p className="xh-category-note">
                本月退款 {money(data.refunds)}，已从月度净支出中扣除。
              </p>
            )}
          </section>

          <section className="xh-panel xh-monthly">
            <div className="xh-panel-heading">
              <div>
                <h2>月度净支出对比</h2>
                <p>含退款抵减 · 负数表示当月退款更多</p>
              </div>
              <span className="xh-panel-mark">03</span>
            </div>
            <div className="xh-chart" aria-label="近12个月支出柱状图">
              {chartState(
                !data.hasHistory,
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.months}
                    margin={{ top: 15, right: 8, bottom: 0, left: 0 }}
                  >
                    <defs>
                      <linearGradient id="xh-bar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#40e3c3" />
                        <stop offset="100%" stopColor="#207fde" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...grid} vertical={false} />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={axis}
                      minTickGap={16}
                      dy={6}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={axis}
                      width={65}
                      tickFormatter={chartMoney}
                    />
                    <Tooltip
                      cursor={{ fill: '#29435a', opacity: 0.3 }}
                      contentStyle={tooltipStyle}
                      labelFormatter={(_, items) =>
                        items?.[0]?.payload?.month || ''
                      }
                      formatter={value => [
                        format(Number(value), 'financial'),
                        '净支出',
                      ]}
                    />
                    <Bar
                      dataKey="expense"
                      fill="url(#xh-bar)"
                      radius={[5, 5, 0, 0]}
                      maxBarSize={24}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>,
              )}
            </div>
          </section>

          <section className="xh-panel xh-recent">
            <div className="xh-panel-heading">
              <div>
                <h2>最近收支</h2>
                <p>{monthLabel} · 最近 6 条明细</p>
              </div>
              <button
                className="xh-text-button"
                type="button"
                onClick={() => go('/accounts')}
              >
                全部
                <Icon name="arrow" />
              </button>
            </div>
            {chartState(
              !data.recent.length,
              <ul className="xh-recent-list">
                {data.recent.map(row => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => go(`/accounts/${row.account}`)}
                    >
                      <span
                        className={`xh-entry-icon ${row.amount >= 0 ? 'income' : 'expense'}`}
                      >
                        <Icon name={row.amount >= 0 ? 'trend' : 'down'} />
                      </span>
                      <span className="xh-entry-copy">
                        <strong>
                          {row.payeeName ||
                            row.notes ||
                            row.categoryName ||
                            '未分类收支'}
                        </strong>
                        <small>
                          {row.date.slice(5).replace('-', '/')} ·{' '}
                          {transactionKind(row)} ·{' '}
                          {row.categoryName || '未分类'} ·{' '}
                          {row.accountName || '记账账户'}
                        </small>
                      </span>
                      <span
                        className={`xh-entry-amount ${row.amount >= 0 ? 'income' : ''}`}
                      >
                        {row.amount > 0 ? '+' : ''}
                        {money(row.amount)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>,
            )}
          </section>
        </div>

        <section className="xh-bottom-strip" aria-label="账本管理">
          <div>
            <span
              className={`xh-status-dot ${failed ? 'error' : loading ? 'loading' : ''}`}
            />
            <span>
              {failed
                ? '等待恢复读取'
                : loading
                  ? '正在读取账本'
                  : '当前账本 · 实时汇总'}
            </span>
          </div>
          <span className="xh-data-note">
            全部账户 · 不含账户间转账和期初余额
          </span>
          <button
            type="button"
            className="xh-text-button"
            onClick={() => go('/settings')}
          >
            <Icon name="settings" />
            账本设置
          </button>
        </section>
        <details className="xh-method">
          <summary>统计口径</summary>
          <p>
            范围为本账本全部账户（含表外和已关闭账户的历史记录），截至所选月份且不晚于今天。账户间转账、还款转账及期初余额不计收支；还款费用应单独记为支出。支出分类的退款冲减净支出，收入分类的冲正冲减净收入。未分类金额暂按流入、流出计入，分类后自动重算。拆分交易按明细汇总金额、按原交易计笔数。支出环图显示退款前发生额；净支出可能为负数。
          </p>
          <p>
            此处“实时汇总”仅表示当前账本数据更新，不代表服务器同步或 Google
            Drive
            备份已完成。上传前请先完成账本同步，备份状态以服务器同步日志为准。
          </p>
        </details>
        {!hidden && data.uncategorized > 0 && (
          <p className="xh-category-note">
            本月有 {data.uncategorized} 条支出明细尚未分类。
            <button type="button" onClick={() => go('/accounts/uncategorized')}>
              去整理
            </button>
          </p>
        )}
        <footer className="xh-footer">
          <span>白野 · 小美 × 财务小花</span>
          <span>YOUR MONEY, YOUR CLARITY</span>
        </footer>
      </div>
    </main>
  );
}
